import React from 'react';
import {
  Box,
  Button,
  Divider,
  Flex,
  useModalContext,
  Tooltip,
  ListItem,
  UnorderedList,
  useDisclosure,
  Spinner,
} from '@chakra-ui/react';
import {BuilderCenteredContent} from '../BuilderCenteredContent';
import {AgentBuilderPageIndicator} from '../AgentBuilderPageIndicator';
import {BuilderFooter} from '../BuilderFooter';
import {useAgentNavigation} from '../AgentBuilderNav.state';
import {useAgentFields} from '../helpers';
import {
  useAgentBuilderServerState,
  useAgentBuilderStore,
} from '../AgentBuilder.state';
import {CreditsSection} from './CreditsSection';
import {FieldsSection} from './FieldsSection';
import {AccountSelectionSection} from './AccountSelectionSection';
import {AutoRefreshSection} from './AutoRefreshSection';
import {SignalsSection} from './SignalsSection';
import {useMutation} from '@tanstack/react-query';
import {usePublishAgent} from '../../../../hooks/api/agents';
import {StepContainer} from '../StepContainer';
import {useTask} from '../../../../hooks/api/tasks';
import {useEnrichAccountsForAgent} from '../hooks/useEnrichAccountsForAgent';
import {useCustomer} from '../../../../hooks/api/metadata';
import {PostSaveAgentModal} from './PostSaveAgentModal';
import {AiFieldDefinition} from '../../../../shared/fields/enrichment';

export const AgentBuilderReviewSettingsStep = () => {
  const {id: agentId} = useAgentBuilderServerState();
  const {primaryField} = useAgentFields(agentId);

  if (!primaryField) {
    return (
      <Flex justifyContent="center" alignItems="center" h="100%">
        <Spinner />
      </Flex>
    );
  }

  return <_AgentBuilderReviewSettingsStep primaryField={primaryField} />;
};

const _AgentBuilderReviewSettingsStep = ({
  primaryField,
}: {
  primaryField: AiFieldDefinition;
}) => {
  const {isSaveable, saveChanges} = useAgentBuilderStore();
  const {status} = useAgentBuilderServerState();

  const {isTaskInProgress: isAccountEnrichmentRunning} = useTask({
    type: 'accountEnrichment',
    fieldDefinitionId: primaryField.id,
  });

  const onNavigate = useAgentNavigation({
    mutation: saveChanges,
    saveOnBack: status === 'draft' && isSaveable(),
  });

  return (
    <StepContainer
      isSaveable={isSaveable() && !isAccountEnrichmentRunning}
      onSaveMutation={saveChanges}
    >
      <Flex direction="column" flex="1">
        <BuilderCenteredContent maxW="650px">
          <AgentBuilderPageIndicator />
          <Flex direction="column" flex="1" gap="6" pb="8">
            <Flex direction="column" gap="2">
              <Box fontSize="xl">Configure Enrichment Details</Box>
              <Box color="kgray.300">
                Before your new agent gets to work enriching accounts (we call
                that a “run!”), fine tune the details for each run.
              </Box>
            </Flex>
            <CreditsSection />
            <Divider />
            <FieldsSection />
            <Divider />
            <AccountSelectionSection />
            <Divider />
            <AutoRefreshSection />
            <Divider />
            <SignalsSection />
          </Flex>
        </BuilderCenteredContent>

        <Divider />

        <BuilderFooter alignItems="baseline" justifyContent="space-between">
          <Button
            isDisabled={isAccountEnrichmentRunning}
            variant="ghost"
            onClick={() => onNavigate('prev')}
          >
            Back
          </Button>

          {isAccountEnrichmentRunning && (
            <Box color="kred.300">
              Agent updates are disabled while account enrichment is running.
            </Box>
          )}

          <SaveButton isDisabled={isAccountEnrichmentRunning} />
        </BuilderFooter>
      </Flex>
    </StepContainer>
  );
};

const SaveButton = ({isDisabled}: {isDisabled: boolean}) => {
  const customer = useCustomer();
  const {onClose} = useModalContext();
  const {accountFilter, label, isValid, saveChanges} = useAgentBuilderStore();
  const {id: agentId, status} = useAgentBuilderServerState();
  const publishAgent = usePublishAgent();
  const postSaveAgentModal = useDisclosure();

  const {
    creditsNeeded,
    insufficientCredits,
    numAccountsToEnrich,
    runEnrichment,
    isPending,
  } = useEnrichAccountsForAgent({accountFilter});

  const saveAndExit = useMutation({
    mutationFn: async () => {
      const shouldPublish = status === 'draft';

      await saveChanges.mutateAsync();
      if (shouldPublish) {
        await publishAgent.mutateAsync({agentId});
      }

      // Run enrichment for newly-published agents, even if there are no
      // accounts to enrich so they see the modal.
      if (!accountFilter || (!numAccountsToEnrich && !shouldPublish)) {
        return 'saveOnly';
      }

      await runEnrichment.mutateAsync();
      return 'saveAndRunEnrichment';
    },
    onSuccess: (result) => {
      if (result === 'saveOnly') {
        onClose();
        return;
      }

      postSaveAgentModal.onOpen();
    },
  });

  return (
    <>
      <PostSaveAgentModal
        agentLabel={label}
        isNewAgent={status === 'draft'}
        isOpen={postSaveAgentModal.isOpen}
        onClose={() => {
          postSaveAgentModal.onClose();
          onClose();
        }}
      />
      <Tooltip
        label={
          <Flex direction="column" gap="2">
            <Box>Not enough credits to run agent.</Box>
            <UnorderedList ms="6">
              <ListItem>
                {creditsNeeded?.toLocaleString()} credits needed.
              </ListItem>
              <ListItem>
                {customer.credits.toLocaleString()} credits available.
              </ListItem>
            </UnorderedList>
          </Flex>
        }
        isDisabled={!insufficientCredits}
      >
        <Button
          colorScheme="kbuttonblue"
          isDisabled={!isValid() || insufficientCredits || isDisabled}
          isLoading={saveAndExit.isPending || isPending}
          onClick={() => saveAndExit.mutate()}
        >
          {!numAccountsToEnrich
            ? status === 'published'
              ? 'Save & Exit'
              : 'Publish & Exit'
            : status === 'published'
              ? 'Save & Run Agent'
              : 'Publish & Run Agent'}
        </Button>
      </Tooltip>
    </>
  );
};
